import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useColorScheme } from '@mui/material/styles';

type Mode = 'light' | 'dark' | 'system';

const modeConfig: { mode: Mode; label: string; icon: React.ReactElement }[] = [
  { mode: 'light', label: 'Light', icon: <LightModeIcon fontSize="small" /> },
  { mode: 'dark', label: 'Dark', icon: <DarkModeIcon fontSize="small" /> },
  { mode: 'system', label: 'System', icon: <SystemIcon fontSize="small" /> },
];

export const ThemeToggle: React.FC = () => {
  const { mode, setMode } = useColorScheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode);
    handleClose();
  };

  const currentIcon = mode === 'dark'
    ? <DarkModeIcon />
    : mode === 'light'
      ? <LightModeIcon />
      : <SystemIcon />;

  return (
    <>
      <Tooltip title="Theme">
        <IconButton
          onClick={handleClick}
          sx={{ color: 'primary.main' }}
          aria-label="Toggle theme"
        >
          {currentIcon}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: { minWidth: 150 },
          },
        }}
      >
        {modeConfig.map(({ mode: m, label, icon }) => (
          <MenuItem
            key={m}
            onClick={() => handleModeChange(m)}
            selected={mode === m}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
            {mode === m && (
              <CheckIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
